/**
 * @file     JourneyNode.tsx
 * @readme   ./README.md
 * @purpose  Single node in the journey map, shows lesson status with custom SVG icons, connector line, and hover animation.
 */
'use client';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import Link from 'next/link';
import { useAppTheme } from '@/core/theme';
import type { Lesson, LessonStatus } from './types';
import { IconLocked, IconAvailable, IconStarted, IconCompleted } from './icons/AcademyIcons';

interface JourneyNodeProps {
  lesson: Lesson;
  status: LessonStatus;
  isActive: boolean;
  showConnector: boolean;
  audienceSlug: string;
  moduleSlug: string;
}

const StatusIcon = ({ status }: { status: LessonStatus }) => {
  switch (status) {
    case 'locked': return <IconLocked size={18} />;
    case 'available': return <IconAvailable size={18} />;
    case 'started': return <IconStarted size={18} />;
    case 'completed': return <IconCompleted size={18} />;
  }
};

export function JourneyNode({ lesson, status, isActive, showConnector, audienceSlug, moduleSlug }: JourneyNodeProps) {
  const { theme } = useAppTheme();
  const dur = theme.motion.durations;
  const ease = theme.motion.easing;

  const isClickable = status !== 'locked';
  const href = `/learn-about-bc-real-estate/${audienceSlug}/${moduleSlug}/${lesson.slug}`;

  const nodeContent = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        px: 2,
        py: 1,
        borderRadius: `${theme.layout.radius.md}px`,
        backgroundColor: isActive ? alpha(theme.rawSemanticColors.interactivePrimary, 0.08) : 'transparent',
        transition: `all ${dur.normal}ms ${ease.standard}`,
        cursor: isClickable ? 'pointer' : 'default',
        opacity: status === 'locked' ? 0.45 : 1,
        transform: 'translateX(0)',
        '&:hover': isClickable ? {
          backgroundColor: alpha(theme.rawSemanticColors.interactivePrimary, 0.05),
          transform: 'translateX(4px)',
        } : {},
      }}
    >
      {/* Node icon + connector */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 24, pt: 0.2 }}>
        <Box sx={{
          transition: `transform ${dur.normal}ms ${ease.emphasized}`,
          '&:hover': isClickable ? { transform: 'scale(1.15)' } : {},
        }}>
          <StatusIcon status={status} />
        </Box>
        {showConnector && (
          <Box
            sx={{
              width: 2,
              height: 20,
              backgroundColor: status === 'completed'
                ? theme.semanticColors.statusSuccess
                : theme.semanticColors.borderSubtle,
              mt: 0.5,
              borderRadius: `${theme.layout.radius.pill}px`,
              transition: `background-color ${dur.normal}ms ${ease.standard}`,
            }}
          />
        )}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: isActive ? 600 : 500,
            color: isActive
              ? theme.semanticColors.interactivePrimary
              : status === 'locked'
                ? theme.semanticColors.textMuted
                : theme.semanticColors.textPrimary,
            lineHeight: 1.3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            transition: `color ${dur.fast}ms ${ease.standard}`,
          }}
        >
          {lesson.titleDefault}
        </Typography>
        <Typography
          sx={{
            fontSize: 11,
            color: theme.semanticColors.textMuted,
            lineHeight: 1.3,
          }}
        >
          {lesson.estimatedMinutes} min · {lesson.xpReward} XP
        </Typography>
      </Box>
    </Box>
  );

  if (isClickable) {
    return (
      <Link href={href} style={{ textDecoration: 'none', color: 'inherit' }}>
        {nodeContent}
      </Link>
    );
  }

  return nodeContent;
}
